import { TextField } from '@refinedev/antd';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { ShowInfoGrid, ShowInfoItem } from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection, ShowSubsection } from '@/components/general/ShowInfo/ShowSection';
import { PaymentMethodFieldsByType, PaymentMethodTypeLabels } from '@/model/General/PaymentMethod';
import { ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';

import { formatBoolean, formatWithSuffix } from './serviceOrderShowHelpers';

/** Labels legibles para los campos del método de pago. */
const PAYMENT_FIELD_LABELS: Record<string, string> = {
  banco: 'Banco',
  nombre_cuenta: 'Titular',
  numero_cuenta: 'Nº de cuenta',
  cbu: 'CBU',
  alias_cbu: 'Alias CBU',
  cuit_cuil: 'CUIT/CUIL',
  clave: 'Clave',
  email_pago: 'Email de pago',
};

interface ServiceOrderShowBillingProps {
  serviceOrder: ServiceOrder;
}

const ServiceOrderShowBilling = (props: ServiceOrderShowBillingProps) => {
  const { t } = useTranslation();
  const { serviceOrder } = props;

  return (
    <ShowSection title={t('Detalles de la facturación')}>
      <ShowSubsection title={t('Tarifas')}>
        <ShowInfoGrid>
          <ShowInfoItem label={t('Precio por hora')}>
            <TextField
              value={formatWithSuffix(
                serviceOrder.hourly_rate,
                `${serviceOrder.currency?.simbolo}/hs`,
              )}
            />
          </ShowInfoItem>
          <ShowInfoItem label={t('Precio por kilómetro')}>
            <TextField
              value={formatWithSuffix(
                serviceOrder.kilometres_rate,
                `${serviceOrder.currency?.simbolo}/km`,
              )}
            />
          </ShowInfoItem>
        </ShowInfoGrid>
      </ShowSubsection>

      <ShowSubsection title={t('Horas y kilómetros')}>
        <ShowInfoGrid>
          <ShowInfoItem label={t('Horas')}>
            <TextField value={formatWithSuffix(serviceOrder.hours_estimated, 'hs')} />
          </ShowInfoItem>
          <ShowInfoItem label={t('Kilómetros recorridos')}>
            <TextField value={formatWithSuffix(serviceOrder.kilometres_estimated, 'km')} />
          </ShowInfoItem>
        </ShowInfoGrid>
      </ShowSubsection>

      <ShowSubsection title={t('Opciones de pago')}>
        <ShowInfoGrid>
          <ShowInfoItem label={t('Pagar servicio al técnico')}>
            <TextField value={formatBoolean(serviceOrder.technician_pay, t('Sí'), t('No'))} />
          </ShowInfoItem>
          <ShowInfoItem label={t('Facturar al cliente')}>
            <TextField value={formatBoolean(serviceOrder.customer_invoice, t('Sí'), t('No'))} />
          </ShowInfoItem>
          {serviceOrder.payment_method && (
            <ShowInfoItem label={t('Método de pago del técnico')} colSpan={2}>
              <div>
                <TextField value={PaymentMethodTypeLabels[serviceOrder.payment_method.tipo]} />
                {PaymentMethodFieldsByType[serviceOrder.payment_method.tipo]
                  .filter(
                    field =>
                      (serviceOrder.payment_method as Record<string, any>)?.[field] != null &&
                      (serviceOrder.payment_method as Record<string, any>)?.[field] !== '',
                  )
                  .map(field => (
                    <div key={field}>
                      <Typography.Text type="secondary">
                        {PAYMENT_FIELD_LABELS[field] ?? field}:{' '}
                      </Typography.Text>
                      <Typography.Text>
                        {(serviceOrder.payment_method as Record<string, any>)[field]}
                      </Typography.Text>
                    </div>
                  ))}
              </div>
            </ShowInfoItem>
          )}
        </ShowInfoGrid>
      </ShowSubsection>

      <ShowSubsection title={t('Cubierto por Acronex')}>
        <ShowInfoGrid>
          <ShowInfoItem label={t('Horas cubiertas por Acronex')}>
            <TextField value={formatWithSuffix(serviceOrder.hours_covered, 'hs')} />
          </ShowInfoItem>
          <ShowInfoItem label={t('Kilómetros cubiertos por Acronex')}>
            <TextField value={formatWithSuffix(serviceOrder.kilometres_covered, 'km')} />
          </ShowInfoItem>
        </ShowInfoGrid>
      </ShowSubsection>

      <ShowInfoItem label={t('Comentarios / Gastos adicionales')} colSpan={2}>
        <TextField value={serviceOrder.aditional_comment ?? '-'} />
      </ShowInfoItem>
    </ShowSection>
  );
};

export default ServiceOrderShowBilling;
